import { AGENT_SCOPE_ACTIONS, type AgentScopeAction } from "../../context/auth-me";
import { Dropdown } from "../../components/ui/dropdown";
import { useAgents, useFolders } from "../home/queries";
import { SCOPE_ACTION_LABELS, type ScopeDraft, type ScopeMode } from "./access-model";

const MODE_OPTIONS: { value: ScopeMode; label: string }[] = [
  { value: "all", label: "All agents" },
  { value: "selected", label: "Selected agents" },
  { value: "none", label: "No agents" },
];

/** Which agents a member may act on, per action. Core caps every action by view. */
export function AgentScopeEditor({ value, onChange, enforced, disabled }: {
  value: ScopeDraft;
  onChange: (next: ScopeDraft) => void;
  /** member_scope mode; "off" records the scope without applying it yet. */
  enforced: "off" | "log" | "enforce";
  disabled?: boolean;
}) {
  const agents = useAgents();
  const folders = useFolders();

  const set = (action: AgentScopeAction, patch: Partial<ScopeDraft[AgentScopeAction]>) =>
    onChange({ ...value, [action]: { ...value[action], ...patch } });
  const toggle = (list: string[], id: string) => (list.includes(id) ? list.filter((x) => x !== id) : [...list, id]);

  return (
    <div className="space-y-3">
      <p className="text-xs text-ink-3">
        Other actions only reach agents the member can also view.
        {enforced !== "enforce" && " Agent access is saved but not enforced for your organization yet."}
      </p>
      {AGENT_SCOPE_ACTIONS.map((action) => {
        const grant = value[action];
        return (
          <div key={action} className="rounded-lg border border-line p-3">
            <div className="flex items-center justify-between gap-3">
              <span className="text-sm font-medium text-ink">{SCOPE_ACTION_LABELS[action]}</span>
              <Dropdown
                ariaLabel={`${SCOPE_ACTION_LABELS[action]} agents`}
                className="w-44"
                value={grant.mode}
                options={MODE_OPTIONS}
                disabled={disabled}
                onChange={(mode) => set(action, { mode })}
              />
            </div>
            {grant.mode === "selected" && (
              <div className="mt-3 grid max-h-48 grid-cols-1 gap-4 overflow-y-auto md:grid-cols-2">
                <fieldset>
                  <legend className="mb-1 text-xs font-semibold uppercase text-ink-3">Folders</legend>
                  {(folders.data ?? []).length === 0 && <p className="text-xs text-ink-3">No folders.</p>}
                  {(folders.data ?? []).map((f) => (
                    <label key={f.folder_id} className="flex items-center gap-2 text-sm text-ink-2">
                      <input
                        type="checkbox"
                        disabled={disabled}
                        checked={grant.folders.includes(f.folder_id)}
                        onChange={() => set(action, { folders: toggle(grant.folders, f.folder_id) })}
                      />
                      {f.name}
                    </label>
                  ))}
                </fieldset>
                <fieldset>
                  <legend className="mb-1 text-xs font-semibold uppercase text-ink-3">Agents</legend>
                  {agents.isLoading && <p className="text-xs text-ink-3">Loading…</p>}
                  {(agents.data ?? []).map((a) => (
                    <label key={a.agent_id} className="flex items-center gap-2 text-sm text-ink-2">
                      <input
                        type="checkbox"
                        disabled={disabled}
                        checked={grant.ids.includes(a.agent_id)}
                        onChange={() => set(action, { ids: toggle(grant.ids, a.agent_id) })}
                      />
                      {a.agent_name || a.name || a.agent_id}
                    </label>
                  ))}
                </fieldset>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
